import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ExternalLink, MapPin, Star, Users, Briefcase } from 'lucide-react';
import { Button, Spinner } from '@/components/ui';
import { useModalStore } from '@/stores';
import { api } from '@/lib/axios';
import { API_ROUTES } from '@/constants';
import type { ProviderCardItem } from '@/types/provider-card';
import styles from './ProviderDetailModal.module.css';

// ── Types for offerings API ──────────────────────────────

interface OfferingSpec {
  attribute_details: { id: string; name: string };
  value: string;
}

interface OfferingCost {
  price: number | string;
  currency: string;
  description?: string;
  pricing_unit_details: { id: string; name: string };
}

interface Offering {
  id: string;
  service_details: { id: string; name: string };
  description: string;
  specifications: OfferingSpec[];
  costs: OfferingCost[];
}

const CURRENCY_SYMBOLS: Record<string, string> = {
  PEN: 'S/.',
  USD: '$',
  EUR: '\u20AC',
  MXN: 'MX$',
};

// ── Offering expandable row ──────────────────────────────

function OfferingRow({ offering }: { offering: Offering }) {
  const [open, setOpen] = useState(false);
  const specs = offering.specifications ?? [];
  const costs = offering.costs ?? [];

  return (
    <div className={`${styles.serviceItem} ${open ? styles.serviceItemOpen : ''}`}>
      <button type="button" className={styles.serviceHeader} onClick={() => setOpen(!open)}>
        <span className={styles.serviceName}>{offering.service_details.name}</span>
        <div className={styles.serviceHeaderRight}>
          {costs.length > 0 && (
            <span className={styles.servicePriceHint}>
              {(CURRENCY_SYMBOLS[costs[0].currency] ?? costs[0].currency)}
              {Number(costs[0].price).toLocaleString()}
            </span>
          )}
          <ChevronDown
            size={16}
            className={`${styles.expandIcon} ${open ? styles.expandIconOpen : ''}`}
          />
        </div>
      </button>
      {open && (
        <div className={styles.serviceBody}>
          {offering.description && <p className={styles.serviceDesc}>{offering.description}</p>}

          {specs.length > 0 && (
            <div className={styles.specSection}>
              <span className={styles.subLabel}>Specifications</span>
              <div className={styles.specsList}>
                {specs.map((spec, i) => (
                  <span key={i} className={styles.specChip}>
                    <span className={styles.specName}>{spec.attribute_details.name}:</span>
                    <span className={styles.specValue}>{spec.value}</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {costs.length > 0 && (
            <div className={styles.specSection}>
              <span className={styles.subLabel}>Pricing</span>
              <div className={styles.pricingList}>
                {costs.map((c, i) => {
                  const sym = CURRENCY_SYMBOLS[c.currency] ?? c.currency;
                  return (
                    <div key={i} className={styles.pricingRow}>
                      <span className={styles.pricingLabel}>
                        {c.description || c.pricing_unit_details.name}
                      </span>
                      <span className={styles.pricingValue}>
                        {sym}{Number(c.price).toLocaleString()} / {c.pricing_unit_details.name}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {!offering.description && specs.length === 0 && costs.length === 0 && (
            <p className={styles.serviceNoDetail}>No additional details available</p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main modal ───────────────────────────────────────────

interface Props {
  provider: ProviderCardItem;
  isSelected?: boolean;
  onToggle?: () => void;
}

export function ProviderDetailModal({ provider, isSelected, onToggle }: Props) {
  const { closeModal } = useModalStore();
  const navigate = useNavigate();

  // Fetch real offerings for this provider
  const { data: offerings, isLoading } = useQuery({
    queryKey: ['modal-offerings', provider.providerId],
    queryFn: async () => {
      const res = await api.get(
        `${API_ROUTES.offerings}?provider=${provider.providerId}&limit=100`,
      );
      return (res.data.results ?? res.data) as Offering[];
    },
    staleTime: 60_000,
  });

  const handleToggle = () => {
    onToggle?.();
    closeModal();
  };

  const handleGoToProvider = () => {
    const confirmed = window.confirm(
      'You will leave the presentation wizard and lose your progress. Continue?',
    );
    if (!confirmed) return;
    closeModal();
    navigate(`/dashboard/providers/${provider.providerId}`);
  };

  return (
    <div className={styles.container}>
      {/* Header: thumbnail + provider info */}
      <div className={styles.header}>
        {provider.image && (
          <img src={provider.image} alt={provider.name} className={styles.thumb} />
        )}
        <div className={styles.headerInfo}>
          <h2 className={styles.name}>{provider.name}</h2>
          <div className={styles.metaRow}>
            {(provider.city || provider.district) && (
              <span className={styles.metaItem}>
                <MapPin size={13} />
                {provider.district ? `${provider.district}, ${provider.city}` : provider.city}
              </span>
            )}
            {provider.rating !== null && (
              <span className={styles.metaItem}>
                <Star size={13} className={styles.starIcon} />
                {provider.rating.toFixed(1)}
              </span>
            )}
            {provider.capacity !== null && provider.capacity > 0 && (
              <span className={styles.metaItem}>
                <Users size={13} />
                {provider.capacity} pax
              </span>
            )}
          </div>
          {provider.description && <p className={styles.description}>{provider.description}</p>}
        </div>
      </div>

      {/* Services / Offerings — fetched from API */}
      <div className={styles.section}>
        <h4 className={styles.sectionTitle}>
          <Briefcase size={14} />
          Services & Offerings
          {offerings && <span className={styles.offeringCount}>{offerings.length}</span>}
        </h4>

        {isLoading ? (
          <div className={styles.loadingRow}><Spinner /></div>
        ) : offerings && offerings.length > 0 ? (
          <div className={styles.servicesList}>
            {offerings.map((o) => (
              <OfferingRow key={o.id} offering={o} />
            ))}
          </div>
        ) : (
          <p className={styles.emptyText}>No offerings registered for this provider</p>
        )}
      </div>

      {/* Actions */}
      <div className={styles.actions}>
        <Button variant="secondary" size="sm" onClick={closeModal}>
          Close
        </Button>
        <Button variant="info" size="sm" onClick={handleGoToProvider}>
          <ExternalLink size={13} />
          View Full Page
        </Button>
        {onToggle && (
          <Button variant={isSelected ? 'warning' : 'primary'} size="sm" onClick={handleToggle}>
            {isSelected ? 'Remove from selection' : 'Add to selection'}
          </Button>
        )}
      </div>
    </div>
  );
}
